import axiosInstance from "../api/axiosInstance";
import { EventType, trackEvent } from "../utils/analytics";
import { assembleBlob } from "../utils/audioStore";
import { handleSubmitNoteForProcessing } from "./notes.service";

interface SessionData {
  sessionUri: string;
  uploadedBytes: number;
  audioId: number;
  contentType: string;
  pendingChunks?: number;
  recordingTime?: number; // Elapsed recording time in seconds for resume functionality
}

/**
 * Isolated session context for finalizePartialUpload operations.
 * This allows multiple finalization operations to run concurrently
 * without interfering with the singleton's state or each other.
 */
interface IsolatedSessionContext {
  sessionUri: string;
  audioId: number;
  uploadedBytes: number;
  contentType: string;
  containerSegments: Blob[];
  totalFileSize: number;
}

export class ResumableUploadManager {
  private sessionUri: string | null = null;
  private uploadedBytes = 0;
  private audioId: number | null = null;
  private contentType: string | null = null;
  private objectName: string | null = null;

  private containerSegments: Blob[] = []; // Complete WebM container segments from MediaRecorder
  private readonly MIN_CHUNK_SIZE = 262144; // 256KB - GCS requires multiples of 256KB
  private isFinalized = false;
  private totalFileSize = 0; // Calculated dynamically during finalization or set from IndexedDB during sync
  private uploadInProgress = false; // Prevent concurrent uploads that corrupt state
  private currentUploadPromise: Promise<void> | null = null; // Promise-based lock for proper synchronization

  // Simple resilient recording flag
  private skipGcsUploads = false; // When true, skip all GCS uploads and rely on IndexedDB

  // Recording time tracking for resume functionality
  private recordingTime = 0; // Elapsed recording time in seconds
  private wasResumed = false; // Flag to indicate if this session was resumed from a previous recording

  /**
   * Calculate the largest multiple of 256KB that fits within the given bytes
   */
  private calculateAlignedChunkSize(totalPendingBytes: number): number {
    return Math.floor(totalPendingBytes / this.MIN_CHUNK_SIZE) * this.MIN_CHUNK_SIZE;
  }

  /**
   * Check if we have enough data to upload an aligned chunk
   */
  private shouldUploadAlignedChunk(totalPendingBytes: number): boolean {
    return totalPendingBytes >= this.MIN_CHUNK_SIZE;
  }

  /**
   * Create an aligned blob from buffered segments
   */
  private createAlignedBlobFromSegments(bytesToExtract: number): { alignedBlob: Blob; remainderBytes: number } {
    // Combine all segments into one blob
    const combinedBlob = new Blob(this.containerSegments);

    // Extract exact bytes needed
    const alignedBlob = combinedBlob.slice(0, bytesToExtract);
    const remainderBytes = combinedBlob.size - bytesToExtract;

    return { alignedBlob, remainderBytes };
  }

  /**
   * Update segment buffer after successful upload by removing uploaded bytes
   */
  private updateSegmentBufferAfterUpload(bytesUploaded: number): void {
    // Create new buffer with remaining data
    const combinedBlob = new Blob(this.containerSegments);
    const remainderBlob = combinedBlob.slice(bytesUploaded);

    // Replace buffer with remainder (empty array if no remainder)
    this.containerSegments = remainderBlob.size > 0 ? [remainderBlob] : [];
  }

  async initiate(payload: {
    title: string;
    note_type?: string;
    category_type?: string;
    modality_mode: string;
    gender_type?: string;
    language_type?: string;
    location_type?: number | null;
    duration_type?: number | null;
    duration_addon_type?: number | null;
    client_id: number | null;
    is_capture: boolean;
    system_metadata: Record<string, unknown>;
    is_locked: boolean; // Added is_locked for locking the recording for non paying users
    content_type: string;
  }): Promise<number> {
    try {
      // Send the full payload including is_locked and content_type
      const response = await axiosInstance.post("/notes/initiate-session-upload/", payload);

      this.audioId = response.data.audio_id;
      this.contentType = payload.content_type;
      this.objectName = response.data.object_name;

      // Directly assign the URL from the backend as the session URI
      this.sessionUri = response.data.url.upload_url;

      if (!this.sessionUri) {
        // Add check in case backend didn't return the URL
        throw new Error("Backend did not return a valid upload_url.");
      }

      this.uploadedBytes = 0;
      this.skipGcsUploads = false; // Reset to normal GCS uploads for new session
      this.saveSession();
      return this.audioId as number;
    } catch (error) {
      console.error("Error initiating upload:", error);
      this.clearSession();
      throw error;
    }
  }

  async uploadChunk(containerSegment: Blob, recordingTime?: number): Promise<void> {
    if (!this.sessionUri) {
      return;
    }

    // Update recording time if provided and save immediately to persist in localStorage
    if (recordingTime !== undefined) {
      this.recordingTime = recordingTime;
      // Save session immediately to persist recordingTime, even if no upload happens yet
      this.saveSession();
    }

    // If GCS uploads are disabled due to network issues, just skip
    if (this.skipGcsUploads) {
      console.log(`Skipping GCS upload due to network issues (${containerSegment.size} bytes) - relying on IndexedDB`);
      return;
    }

    // Add container segment to buffer (preserves WebM container boundaries)
    this.containerSegments.push(containerSegment);
    // Note: totalFileSize will be calculated dynamically during finalization to avoid race conditions

    // Calculate total pending size
    const totalPendingSize = this.containerSegments.reduce((total, segment) => total + segment.size, 0);

    // Prevent concurrent uploads - if an upload is already in progress, just buffer and return
    if (this.uploadInProgress) {
      console.log(
        `Upload already in progress, buffering chunk (${containerSegment.size} bytes). Total pending: ${totalPendingSize} bytes`,
      );
      return;
    }

    // Only upload if we can create an aligned chunk (multiple of 256KB)
    if (this.shouldUploadAlignedChunk(totalPendingSize)) {
      const alignedSize = this.calculateAlignedChunkSize(totalPendingSize);
      const numChunks = alignedSize / this.MIN_CHUNK_SIZE;
      console.log(`Uploading aligned chunk: ${alignedSize} bytes (${numChunks} × 256KB)`);

      // Create a Promise for this upload operation that can be awaited by completeGcsUpload
      this.currentUploadPromise = (async () => {
        try {
          this.uploadInProgress = true; // Set lock before upload
          await this.uploadAlignedContainerSegments(alignedSize);
        } catch (error) {
          // Network or GCS failure - stop all further GCS uploads
          console.warn(
            "GCS upload failed, disabling further GCS uploads and relying on IndexedDB:",
            error,
            this.audioId,
          );
          this.skipGcsUploads = true;

          // Clear the chunk buffer since we won't be uploading to GCS anymore
          this.containerSegments = [];
        } finally {
          this.uploadInProgress = false; // Always release lock
          this.currentUploadPromise = null; // Clear the promise
        }
      })();

      await this.currentUploadPromise;
    } else {
      const bytesNeeded = this.MIN_CHUNK_SIZE - totalPendingSize;
      console.log(
        `Buffering container segment (${containerSegment.size} bytes). Total pending: ${totalPendingSize} bytes (need ${bytesNeeded} more for upload)`,
      );
    }
  }

  /**
   * Upload an aligned chunk (exact multiple of 256KB) to GCS
   * This eliminates bandwidth waste by ensuring GCS accepts all uploaded bytes
   * Implements incremental retry: 2s delay, then 4s delay (3 total attempts)
   */
  private async uploadAlignedContainerSegments(exactBytesToUpload: number): Promise<void> {
    if (this.containerSegments.length === 0) {
      throw new Error("No container segments to upload");
    }

    if (!this.sessionUri) {
      throw new Error("Session URI is null - cannot upload container segments");
    }

    // Verify alignment
    if (exactBytesToUpload % this.MIN_CHUNK_SIZE !== 0) {
      throw new Error(`Upload size must be multiple of 256KB. Got ${exactBytesToUpload} bytes`);
    }

    // Create aligned blob from segments. These are `let`-bound (not const)
    // because when GCS partially persists a 308 response, we advance
    // uploadedBytes to the confirmed boundary, re-slice only the unconfirmed
    // tail, and recompute the Content-Range for the next attempt.
    let { alignedBlob } = this.createAlignedBlobFromSegments(exactBytesToUpload);
    let bytesInBody = alignedBlob.size;
    let startByte = this.uploadedBytes;
    let endByte = startByte + bytesInBody - 1;

    console.log(`Uploading aligned chunk: bytes ${startByte}-${endByte} (${bytesInBody} bytes)`);

    // Incremental retry configuration: [2000ms, 4000ms]
    const retryDelays = [2000, 4000];
    const maxAttempts = 3; // Original attempt + 2 retries

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        if (!this.sessionUri) {
          throw new Error("Session URI became null during retry loop");
        }

        // Make the upload attempt with EXACT SAME data each time
        const response = await fetch(this.sessionUri, {
          method: "PUT",
          headers: {
            "Content-Range": `bytes ${startByte}-${endByte}/*`,
            "Content-Type": this.contentType || "audio/webm",
          },
          body: alignedBlob,
        });

        // Handle successful responses
        if (response.status === 308) {
          // Partial upload success - verify server confirmed our bytes
          const rangeHeader = response.headers.get("Range");
          let serverConfirmedBytes = this.uploadedBytes;

          if (rangeHeader) {
            const match = rangeHeader.match(/bytes=0-(\d+)/);
            if (match) {
              serverConfirmedBytes = Number.parseInt(match[1], 10) + 1;
            }
          }

          // Only update position if server confirmed our upload
          const expectedBytes = startByte + bytesInBody;
          if (serverConfirmedBytes >= expectedBytes) {
            this.uploadedBytes = serverConfirmedBytes;
            this.updateSegmentBufferAfterUpload(bytesInBody);
            const attemptLabel = attempt > 1 ? `[RETRY ${attempt - 1}]` : "";
            console.log(`${attemptLabel} Aligned upload successful. Server confirmed: ${this.uploadedBytes} bytes`);
            this.saveSession();
            return;
          }

          // GCS only persists in 256 KB-aligned increments. Branch on the
          // confirmed delta:
          //   - delta === 0: GCS accepted nothing this attempt (transient
          //     network glitch). Retry the same body unchanged.
          //   - 0 < delta < bytesInBody, 256 KB-aligned: GCS persisted a
          //     prefix and dropped the rest. Advance our position to the
          //     confirmed boundary and retry only the unconfirmed tail.
          //   - otherwise: genuine mismatch, fall through to throw.
          const persistedDelta = serverConfirmedBytes - startByte;
          if (persistedDelta === 0 && attempt < maxAttempts) {
            const delay = retryDelays[attempt - 1];
            console.warn(
              `[RETRY ${attempt}/${maxAttempts - 1}] GCS accepted no bytes for this attempt; retrying same ${bytesInBody}-byte body after ${delay}ms.`,
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }

          if (
            persistedDelta > 0 &&
            persistedDelta < bytesInBody &&
            persistedDelta % this.MIN_CHUNK_SIZE === 0 &&
            attempt < maxAttempts
          ) {
            const delay = retryDelays[attempt - 1];
            console.warn(
              `[RETRY ${attempt}/${maxAttempts - 1}] Partial GCS persist: confirmed ${serverConfirmedBytes}, expected ${expectedBytes}. Advancing and retrying tail.`,
            );
            this.uploadedBytes = serverConfirmedBytes;
            this.updateSegmentBufferAfterUpload(persistedDelta);
            this.saveSession();

            // Re-slice the unconfirmed tail for the next attempt.
            const remainingBytes = bytesInBody - persistedDelta;
            const reslice = this.createAlignedBlobFromSegments(remainingBytes);
            alignedBlob = reslice.alignedBlob;
            bytesInBody = alignedBlob.size;
            if (bytesInBody !== remainingBytes) {
              throw new Error(`Tail re-slice produced ${bytesInBody} bytes, expected ${remainingBytes}`);
            }
            startByte = this.uploadedBytes;
            endByte = startByte + bytesInBody - 1;

            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }

          // Server didn't confirm expected bytes - this is an error condition
          console.warn(
            `Server confirmation mismatch after retries: expected ${expectedBytes}, got ${serverConfirmedBytes}`,
          );
          throw new Error(`Server only confirmed ${serverConfirmedBytes} bytes, expected ${expectedBytes}`);
        }

        if (response.status === 200 || response.status === 201) {
          // Complete upload success. Compute the final size from the current
          // startByte/bytesInBody rather than the original exactBytesToUpload
          // parameter — after a tail-retry these values reflect only the
          // remaining slice, while uploadedBytes has already absorbed the
          // earlier confirmed prefix.
          const actualTotalSize = startByte + bytesInBody;
          this.uploadedBytes = actualTotalSize;
          this.containerSegments = [];
          const attemptLabel = attempt > 1 ? `[RETRY ${attempt - 1}]` : "";
          console.log(`${attemptLabel} Upload completed successfully. Total size: ${actualTotalSize} bytes`);
          return;
        }

        // Handle retryable errors (500, 502, 503)
        if ([503, 500, 502].includes(response.status)) {
          if (attempt < maxAttempts) {
            const delay = retryDelays[attempt - 1];
            console.log(
              `[RETRY ${attempt}/${maxAttempts - 1}] Got ${response.status}, retrying after ${delay}ms delay...`,
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue; // Try next attempt
          }
          // All retries exhausted
          const errorText = await response.text();
          console.warn(`[RETRY EXHAUSTED] All ${maxAttempts} attempts failed with ${response.status}: ${errorText}`);
          throw new Error(`All retries failed: HTTP ${response.status}`);
        }

        // Non-retryable HTTP errors - fail immediately
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      } catch (error) {
        // Check if this is an HTTP error we already handled
        if (error instanceof Error && error.message.includes("HTTP")) {
          throw error; // Re-throw - will trigger resilient mode
        }

        // Network errors - retry with incremental backoff
        if (attempt < maxAttempts) {
          const delay = retryDelays[attempt - 1];
          console.log(`[RETRY ${attempt}/${maxAttempts - 1}] Network error, retrying after ${delay}ms delay...`, error);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue; // Try next attempt
        }
        // All retries exhausted for network errors
        console.error(`[RETRY EXHAUSTED] All ${maxAttempts} attempts failed with network error:`, error);
        throw error; // Will trigger resilient mode
      }
    }

    // Should never reach here, but add safety
    throw new Error("Upload failed after all retry attempts");
  }

  /**
   * Upload final chunk during finalization (can be any size)
   * Used for the last remaining bytes that don't need to be aligned
   */
  private async uploadFinalChunk(): Promise<void> {
    if (this.containerSegments.length === 0) {
      console.log("No final chunks to upload");
      return;
    }

    if (!this.sessionUri) {
      throw new Error("Session URI is null - cannot upload final chunk");
    }

    // Combine all remaining segments
    const finalBlob = new Blob(this.containerSegments);
    const finalSize = finalBlob.size;

    if (finalSize === 0) {
      console.log("Final chunk is empty, skipping");
      return;
    }

    const startByte = this.uploadedBytes;
    const endByte = startByte + finalSize - 1;

    // Calculate the actual total file size based on current position + final chunk
    const actualTotalSize = this.uploadedBytes + finalSize;

    // Include actual total file size in Content-Range for final chunk
    const contentRange = `bytes ${startByte}-${endByte}/${actualTotalSize}`;

    console.log(`Final chunk Content-Range: ${contentRange}`);

    const response = await fetch(this.sessionUri, {
      method: "PUT",
      headers: {
        "Content-Range": contentRange,
        "Content-Type": this.contentType || "audio/webm",
      },
      body: finalBlob,
    });

    if (response.status === 200 || response.status === 201) {
      // Upload complete
      this.uploadedBytes = actualTotalSize;
      this.containerSegments = [];
      console.log("Final chunk uploaded successfully. Upload complete.");
      return;
    }

    if (response.status === 308) {
      // Shouldn't happen for final chunk, but handle it
      this.uploadedBytes += finalSize;
      this.containerSegments = [];
      console.log("Final chunk uploaded (308 response)");
      return;
    }

    const errorText = await response.text();
    console.error(`Final chunk upload failed. Status: ${response.status}, Response: ${errorText}`);
    console.log(`Content-Range used: ${contentRange}`);
    console.log(`Final chunk size: ${finalSize}, Start byte: ${startByte}, Total size: ${actualTotalSize}`);
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  async checkUploadStatus(): Promise<void> {
    try {
      if (!this.sessionUri) {
        throw new Error("Session URI became null before status check fetch."); // Safety check
      }
      const statusResponse = await fetch(this.sessionUri, {
        method: "PUT",
        headers: {
          "Content-Range": "bytes */*", // Query status
        },
      });

      if (statusResponse.status === 308) {
        // Resume Incomplete
        const range = statusResponse.headers.get("Range");
        if (range) {
          const match = range.match(/bytes=0-(\d+)/);
          if (match) {
            const serverAcknowledgedBytes = Number.parseInt(match[1], 10);
            // Update only if server value is greater or equal, avoid decreasing
            if (serverAcknowledgedBytes + 1 >= (this.uploadedBytes ?? 0)) {
              this.uploadedBytes = serverAcknowledgedBytes + 1;
              console.log(`Upload status checked: Resuming from byte ${this.uploadedBytes}`);
              this.saveSession(); // Save potentially corrected position
            } else {
              console.log(
                `Server range (${range}) is less than current position (${this.uploadedBytes}). Keeping current position.`,
              );
            }
          } else {
            console.log("Received 308 status but Range header was missing or invalid. Cannot confirm uploaded bytes.");
          }
        } else {
          // 308 without Range header indicates 0 bytes uploaded
          console.log("Received 308 status without Range header. Assuming 0 bytes uploaded.");
          this.uploadedBytes = 0;
          this.saveSession();
        }
      } else if (statusResponse.status === 200 || statusResponse.status === 201) {
        console.log("Upload status check found upload already completed.");
      } else if (statusResponse.status === 400) {
        // Handle specific case where GCS session is corrupted due to alignment issues
        const errorBody = await statusResponse.text();
        console.error(`GCS session corrupted due to alignment issues: ${errorBody}`);

        // Check if this is the specific alignment error
        if (errorBody.includes("262144") && errorBody.includes("bytes uploaded is required")) {
          console.log("Detected GCS alignment corruption - session will be reset for fresh upload");
          throw new Error("GCS_SESSION_CORRUPTED_ALIGNMENT");
        }

        console.error(`Error checking GCS upload status: ${statusResponse.status}`, errorBody);
        throw new Error(`Unexpected status ${statusResponse.status} when checking upload status.`);
      } else {
        // Consider non-recoverable errors (e.g., 4xx, 5xx)
        if (statusResponse.status === 404 || statusResponse.status === 410) {
          this.clearSession();
          throw new Error(`Upload session not found or expired (status ${statusResponse.status}).`);
        }
        const errorBody = await statusResponse.text();
        console.error(`Error checking GCS upload status: ${statusResponse.status}`, errorBody);
        throw new Error(`Unexpected status ${statusResponse.status} when checking upload status.`);
      }
    } catch (error) {
      console.error("Network or other error checking upload status:", error);
      throw error; // Re-throw to signal failure
    }
  }

  async completeGcsUpload(): Promise<void> {
    if (!this.sessionUri || !this.audioId) {
      throw new Error("No active upload session URI or audioId to complete with GCS.");
    }

    console.log("Starting GCS upload completion");

    // Wait for any in-progress upload to complete before finalizing
    if (this.currentUploadPromise) {
      console.log("Waiting for in-progress upload to complete before finalization...");
      await this.currentUploadPromise;
      console.log("In-progress upload completed, continuing with finalization");
    }

    // If we were in resilient mode, sync everything from IndexedDB
    if (this.skipGcsUploads) {
      console.log("Was in resilient mode - syncing complete audio from IndexedDB");

      // Capture state before sync for analytics
      const uploadedBytesBeforeSync = this.uploadedBytes;

      await this.syncFromIndexedDB();

      // Track the resilient sync event with proper metadata
      trackEvent(EventType.GCS_INDEXEDDB_SYNC, {
        audio_id: this.audioId,
        uploaded_bytes_before_sync: uploadedBytesBeforeSync,
        missing_bytes_synced: this.totalFileSize - uploadedBytesBeforeSync,
        sync_successful: true,
      });
      return;
    }

    // Normal finalization with any remaining chunks
    // Set uploadInProgress lock to prevent concurrent uploads from uploadChunk
    // Any chunks arriving during finalization will be buffered instead of uploaded
    this.uploadInProgress = true;

    const currentPendingSize = this.containerSegments.reduce((total, segment) => total + segment.size, 0);
    console.log(
      `Normal finalization: uploadedBytes=${this.uploadedBytes}, pendingInBuffer=${currentPendingSize}, segments=${this.containerSegments.length}`,
    );

    // Try to upload any remaining aligned chunks first
    if (this.shouldUploadAlignedChunk(currentPendingSize)) {
      const alignedSize = this.calculateAlignedChunkSize(currentPendingSize);
      console.log(`Uploading final aligned chunks: ${alignedSize} bytes`);

      try {
        await this.uploadAlignedContainerSegments(alignedSize);
      } finally {
        this.uploadInProgress = false; // Release lock after upload
      }
    } else {
      // No upload needed, release lock
      this.uploadInProgress = false;
    }

    // Upload any remaining unaligned final chunk
    const finalPendingSize = this.containerSegments.reduce((total, segment) => total + segment.size, 0);
    if (this.containerSegments.length > 0) {
      const remainingSize = this.containerSegments.reduce((total, segment) => total + segment.size, 0);
      console.log(`Uploading final unaligned chunk: ${remainingSize} bytes`);

      // Calculate expected total for final chunk upload
      const expectedTotal = this.uploadedBytes + finalPendingSize;
      this.totalFileSize = expectedTotal;

      this.uploadInProgress = true; // Set lock for final chunk
      try {
        await this.uploadFinalChunk();
      } finally {
        this.uploadInProgress = false; // Release lock
      }
    }

    // Verify upload completion - all pending chunks should have been uploaded
    const remainingPending = this.containerSegments.reduce((total, segment) => total + segment.size, 0);
    if (remainingPending > 0) {
      console.error(`Upload incomplete: ${remainingPending} bytes still pending in buffer after finalization`);
      throw new Error(
        `Upload incomplete. ${remainingPending} bytes still pending in buffer after all upload attempts.`,
      );
    }

    console.log(`GCS upload completed successfully. Total uploaded: ${this.uploadedBytes} bytes`);
  }

  /**
   * Sync complete audio from IndexedDB to GCS when we were in resilient mode
   */
  private async syncFromIndexedDB(): Promise<void> {
    console.log("Syncing complete audio from IndexedDB to GCS");

    // Get complete audio from IndexedDB (source of truth)
    if (!this.audioId) {
      throw new Error("AudioId is null during IndexedDB sync");
    }
    const fullBlob = await assembleBlob(this.audioId.toString());
    if (!fullBlob || fullBlob.size === 0) {
      throw new Error("No audio data found in IndexedDB");
    }

    const totalBytes = fullBlob.size;
    console.log(`IndexedDB contains ${totalBytes} bytes total`);

    // Important: Update totalFileSize to match actual IndexedDB data
    // This prevents Content-Range header mismatches
    this.totalFileSize = totalBytes;

    // Try to check what GCS actually has, but handle invalid session gracefully
    try {
      await this.checkUploadStatus();
      console.log(`GCS currently has ${this.uploadedBytes} bytes`);
    } catch (error) {
      if (error instanceof Error && error.message === "GCS_SESSION_CORRUPTED_ALIGNMENT") {
        console.warn("GCS session corrupted due to alignment issues, starting fresh upload");
        this.uploadedBytes = 0;
      } else {
        console.warn("GCS upload session is invalid, starting fresh upload:", error);
        // If the session is invalid due to other reasons, reset and upload everything
        this.uploadedBytes = 0;
      }
    }

    if (this.uploadedBytes >= totalBytes) {
      console.log("GCS already has all the data, no sync needed");
      return;
    }

    // Upload missing portion in aligned chunks
    const missingBytes = totalBytes - this.uploadedBytes;
    const missingData = fullBlob.slice(this.uploadedBytes);
    console.log(`Uploading ${missingBytes} missing bytes from IndexedDB`);

    // Set up for sync upload
    this.containerSegments = [missingData];

    // Upload in aligned chunks
    while (this.containerSegments.length > 0) {
      const pendingSize = this.containerSegments.reduce((total, segment) => total + segment.size, 0);

      if (pendingSize === 0) break;

      if (this.shouldUploadAlignedChunk(pendingSize)) {
        const alignedSize = this.calculateAlignedChunkSize(pendingSize);
        console.log(`Uploading aligned sync chunk: ${alignedSize} bytes`);
        await this.uploadAlignedContainerSegments(alignedSize);
      } else {
        // Final unaligned chunk
        console.log(`Uploading final sync chunk: ${pendingSize} bytes`);
        await this.uploadFinalChunk();
        break;
      }
    }

    console.log("IndexedDB sync completed successfully");
  }

  async finalizeUpload(isLocked = false, recordingSeconds?: number): Promise<void> {
    // IMPORTANT: Don't set isFinalized here - let chunks continue to arrive and be processed
    // Moving this to the END ensures late-arriving chunks are still buffered and uploaded

    if (!this.sessionUri || !this.audioId) {
      console.warn("Finalize called without active session URI or audio ID.");
      throw new Error("No active upload session to finalize.");
    }

    try {
      // Complete the GCS upload (will sync from IndexedDB if needed)
      await this.completeGcsUpload();

      // Then notify backend that upload is complete
      const formData = new FormData();
      formData.append("audio_id", this.audioId?.toString() ?? "");
      formData.append("is_locked", isLocked.toString());
      formData.append("content_type", this.contentType ?? "audio/webm");
      formData.append("was_resumed", this.wasResumed.toString());
      if (recordingSeconds) {
        formData.append("recording_seconds", recordingSeconds.toString());
      }

      await axiosInstance.post("/notes/capture_audio/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log(
        `Backend notified of completion for audio ID: ${this.audioId}, isLocked: ${isLocked}, wasResumed: ${this.wasResumed}`,
      );

      // NOW mark as finalized (after everything is complete)
      this.isFinalized = true;

      // Clear the session after successful completion
      this.clearSession();

      console.log("GCS upload and backend notification completed successfully");
    } catch (error) {
      console.error("Error during final GCS PUT request or backend notification:", error);
      throw error;
    }
  }

  private saveSession(recordingTime?: number) {
    // Update internal recordingTime if provided
    if (recordingTime !== undefined) {
      this.recordingTime = recordingTime;
    }

    // Guard: Don't save if essential fields are missing
    if (!this.sessionUri || !this.audioId || !this.contentType) {
      console.log(this.sessionUri, this.audioId, this.contentType);
      console.warn("[SAVE_SESSION] Skipping save - missing required fields");
      return;
    }

    const sessionData: SessionData = {
      sessionUri: this.sessionUri,
      uploadedBytes: this.uploadedBytes,
      audioId: this.audioId,
      contentType: this.contentType,
      pendingChunks: this.containerSegments.length, // saving count for debugging - not really needed
      recordingTime: this.recordingTime,
    };

    // Also save by audioId for multiple session support - this is to support process now for multiple audio files
    const sessionKey = `resumableUpload_${this.audioId}`;
    localStorage.setItem(sessionKey, JSON.stringify(sessionData));
  }

  public async cancelGcsUpload(): Promise<void> {
    if (!this.sessionUri) {
      console.log("No active GCS session to cancel");
      return;
    }

    const sessionUriToCancel = this.sessionUri;
    const audioIdToLog = this.audioId;

    try {
      console.log(`Cancelling GCS upload session for audio ID: ${audioIdToLog}`);

      const response = await fetch(sessionUriToCancel, {
        method: "DELETE",
        headers: {
          "Content-Length": "0",
        },
      });

      if (response.status === 499) {
        console.log(`GCS upload session cancelled successfully for audio ID: ${audioIdToLog}`);
      } else {
        console.warn(`Unexpected status ${response.status} when cancelling GCS upload. Expected 499.`);
      }
    } catch (error) {
      console.error(`Error cancelling GCS upload session for audio ID: ${audioIdToLog}:`, error);
      // Don't throw the error - cancellation is best-effort
    } finally {
      // Always clear local session regardless of GCS cancellation result
      this.clearSession();
    }
  }

  public clearSession(specificAudioId?: number) {
    if (specificAudioId) {
      // Clear only the specific session
      const sessionKey = `resumableUpload_${specificAudioId}`;
      localStorage.removeItem(sessionKey);
      console.log(`Cleared specific resumable session for audioId: ${specificAudioId}`);
      return;
    }

    const audioId = this.audioId; // Store for logging
    localStorage.removeItem("resumableUpload"); // this is for backward compatibility

    // Also clear the audioId-specific session if we have one
    if (this.audioId) {
      const sessionKey = `resumableUpload_${this.audioId}`;
      localStorage.removeItem(sessionKey);
    }

    this.sessionUri = null;
    this.uploadedBytes = 0;
    this.audioId = null;
    this.contentType = null;
    this.objectName = null;
    this.containerSegments = [];
    this.isFinalized = false;
    this.totalFileSize = 0;
    this.skipGcsUploads = false; // Reset resilient flag
    this.uploadInProgress = false; // Reset upload lock
    this.recordingTime = 0; // Reset recording time
    this.wasResumed = false; // Reset resumed flag
    console.log(`Cleared local resumable session state (was for audioId: ${audioId})`);
  }

  /**
   * Force upload any pending aligned container segments during device switching
   */
  async flushPendingChunks(): Promise<void> {
    if (this.skipGcsUploads) {
      console.log("Skipping flush - GCS uploads disabled, will sync from IndexedDB during finalization");
      return;
    }

    if (this.containerSegments.length === 0 || this.isFinalized) {
      return;
    }

    // Wait for any in-progress upload to complete before flushing
    if (this.uploadInProgress) {
      console.log("Upload in progress, waiting before flush...");
      // Wait briefly for upload to complete
      await new Promise((resolve) => setTimeout(resolve, 100));
      // If still in progress after wait, skip flush (will be handled during finalization)
      if (this.uploadInProgress) {
        console.log("Upload still in progress, skipping flush");
        return;
      }
    }

    const totalPendingSize = this.containerSegments.reduce((total, segment) => total + segment.size, 0);

    if (this.shouldUploadAlignedChunk(totalPendingSize)) {
      const alignedSize = this.calculateAlignedChunkSize(totalPendingSize);
      console.log(`Flushing aligned chunks during device switch: ${alignedSize} bytes`);

      try {
        this.uploadInProgress = true; // Set lock
        await this.uploadAlignedContainerSegments(alignedSize);
        console.log("Successfully flushed aligned chunks");
      } catch (error) {
        console.warn("Failed to flush aligned chunks during device switch:", error);
        // Enter resilient mode if flush fails
        this.skipGcsUploads = true;
        this.containerSegments = [];
      } finally {
        this.uploadInProgress = false; // Release lock
      }
    } else {
      console.log(`Skipping flush - only ${totalPendingSize} bytes pending (need ${this.MIN_CHUNK_SIZE} for upload)`);
    }
  }

  getAudioId(): number | null {
    return this.audioId;
  }

  /**
   * Get debugging information about the current upload state
   */
  getDebugInfo(): object {
    return {
      sessionUri: !!this.sessionUri,
      uploadedBytes: this.uploadedBytes,
      totalFileSize: this.totalFileSize,
      audioId: this.audioId,
      objectName: this.objectName,
      contentType: this.contentType,
      pendingSegments: this.containerSegments.length,
      totalPendingSize: this.containerSegments.reduce((total, segment) => total + segment.size, 0),
      isFinalized: this.isFinalized,
      skipGcsUploads: this.skipGcsUploads,
    };
  }

  hasActiveSession(): boolean {
    // Check both URI and audioId for a more robust active state check
    return this.sessionUri !== null && this.audioId !== null;
  }

  /**
   * Check if there's a partial upload available for finalization for a specific audio ID
   * This happens when user refreshed during recording and there's a saved session with uploaded bytes
   */
  hasPartialUploadAvailable(audioId: number): boolean {
    const sessionKey = `resumableUpload_${audioId}`;
    const savedSession = localStorage.getItem(sessionKey);
    if (!savedSession) return false;

    try {
      const session = JSON.parse(savedSession);
      return session.sessionUri && session.audioId === audioId && session.uploadedBytes >= 0;
    } catch (e) {
      console.error(`Failed to parse saved session for audio ID ${audioId}`, e);
      return false;
    }
  }

  /**
   * Get information about the partial upload for UI display for a specific audio ID
   */
  getPartialUploadInfo(audioId: number): {
    audioId: number | null;
    uploadedBytes: number;
    objectName: string | null;
  } | null {
    if (!this.hasPartialUploadAvailable(audioId)) return null;

    const sessionKey = `resumableUpload_${audioId}`;
    const savedSession = localStorage.getItem(sessionKey);
    if (!savedSession) return null;

    try {
      const session = JSON.parse(savedSession);
      return {
        audioId: session.audioId,
        uploadedBytes: session.uploadedBytes || 0,
        objectName: session.objectName,
      };
    } catch (e) {
      console.error(`Failed to parse saved session for audio ID ${audioId}`, e);
      return null;
    }
  }

  /**
   * Finalize a partial upload without adding new chunks
   * Used for "Process Now" functionality when user refreshed during recording
   *
   * IMPORTANT: This method uses isolated context to avoid modifying singleton state.
   * This allows multiple finalizePartialUpload calls to run concurrently without
   * interfering with each other or with an active recording session.
   */
  async finalizePartialUpload(audioId: number, gcsUploadUrl?: string): Promise<void> {
    console.log(`[ISOLATED] Starting finalization of partial GCS upload for audio ID: ${audioId}`);

    // Load the specific session for this audioId
    const sessionKey = `resumableUpload_${audioId}`;
    const savedSession = localStorage.getItem(sessionKey);

    let sessionData: SessionData | null = null;
    if (savedSession) {
      try {
        sessionData = JSON.parse(savedSession) as SessionData;
      } catch (e) {
        console.warn(`[ISOLATED] Invalid session data for audio ID: ${audioId}`, e);
      }
    }

    // Determine session URI from either sessionData or gcsUploadUrl parameter
    const sessionUri = sessionData?.sessionUri || gcsUploadUrl;

    if (!sessionUri) {
      throw new Error(
        `No GCS session URI found for audio ID: ${audioId} (neither in localStorage nor system_metadata)`,
      );
    }

    // Create isolated context - this does NOT modify the singleton's state
    const ctx: IsolatedSessionContext = {
      sessionUri,
      audioId: sessionData?.audioId || audioId,
      uploadedBytes: sessionData?.uploadedBytes || 0,
      contentType: sessionData?.contentType || "audio/webm",
      containerSegments: [],
      totalFileSize: 0,
    };

    console.log(
      `[ISOLATED] Created isolated context for audio ID ${audioId}: ` +
        `sessionUri=${sessionUri.substring(0, 50)}..., uploadedBytes=${ctx.uploadedBytes}`,
    );

    try {
      // Try to sync complete audio from IndexedDB for partial uploads
      try {
        await this.syncFromIndexedDBIsolated(ctx);
        console.log(`[ISOLATED] Successfully synced audio from IndexedDB for audio ID: ${audioId}`);
      } catch (indexedDBError) {
        console.warn(`[ISOLATED] IndexedDB sync failed for audio ID ${audioId}:`, indexedDBError);
        console.log(`[ISOLATED] Falling back to GCS-only finalization for audio ID: ${audioId}`);

        // Fallback: Finalize what's already in GCS without IndexedDB
        await this.finalizeGCSOnlyIsolated(ctx);
      }

      // Notify backend to process the finalized partial upload
      await handleSubmitNoteForProcessing({
        audio_id: ctx.audioId,
      });

      console.log(`[ISOLATED] Partial GCS upload finalized successfully for audio ID: ${audioId}`);

      // Clear only the specific session from localStorage
      if (sessionData) {
        localStorage.removeItem(sessionKey);
      }
    } catch (error) {
      console.error(`[ISOLATED] Error finalizing partial GCS upload for audio ID ${audioId}:`, error);
      throw error;
    }
    // NOTE: No finally block needed - we never modified singleton state
  }

  /**
   * Validate if a GCS session is still active and not expired
   * Used before resuming a recording to check if the GCS session is still valid
   * @param audioId The audio ID to validate
   * @returns true if session is valid, false if expired or invalid
   *
   * NOTE: Uses isolated context to avoid modifying singleton state
   */
  async validateSession(audioId: number): Promise<boolean> {
    const sessionKey = `resumableUpload_${audioId}`;
    const savedSession = localStorage.getItem(sessionKey);

    if (!savedSession) {
      console.log(`[VALIDATION] No session found for audioId: ${audioId}`);
      return false;
    }

    try {
      const session = JSON.parse(savedSession) as SessionData;

      // Create isolated context - does NOT modify singleton state
      const ctx: IsolatedSessionContext = {
        sessionUri: session.sessionUri,
        audioId: session.audioId,
        uploadedBytes: session.uploadedBytes || 0,
        contentType: session.contentType || "audio/webm",
        containerSegments: [],
        totalFileSize: 0,
      };

      try {
        // Check with GCS if session is still valid using isolated method
        await this.checkUploadStatusIsolated(ctx);
        console.log(`[VALIDATION] Session is valid for audioId: ${audioId}`);
        return true;
      } catch (error) {
        if (error instanceof Error) {
          // Check for expired session errors (404, 410, or expired message)
          if (
            error.message.includes("404") ||
            error.message.includes("410") ||
            error.message.includes("expired") ||
            error.message.includes("not found")
          ) {
            console.log(`[VALIDATION] Session expired for audioId: ${audioId}`);
            return false;
          }
        }
        // Other errors might be temporary - return false to be safe
        console.warn(`[VALIDATION] Error checking session for audioId: ${audioId}`, error);
        return false;
      }
    } catch (error) {
      console.error(`[VALIDATION] Failed to parse session for audioId ${audioId}`, error);
      return false;
    }
  }

  /**
   * Get session information for resume functionality
   * Returns recordingTime and uploadedBytes from localStorage
   * @param audioId The audio ID to get info for
   * @returns Session info object or null if not found
   */
  getSessionInfoForResume(audioId: number): { recordingTime: number; uploadedBytes: number } | null {
    // We could also send metadata like recordingTime and uploadedBytes to GCS in the uploadChunk request and restore it here, but it's not worth the extra overhead for this use case
    const sessionKey = `resumableUpload_${audioId}`;
    const savedSession = localStorage.getItem(sessionKey);

    if (!savedSession) {
      console.log(`[RESUME] No session info found for audioId: ${audioId}`);
      return null;
    }

    try {
      const session = JSON.parse(savedSession) as SessionData;
      return {
        recordingTime: session.recordingTime || 0,
        uploadedBytes: session.uploadedBytes || 0,
      };
    } catch (error) {
      console.error(`[RESUME] Failed to parse session for audioId ${audioId}`, error);
      return null;
    }
  }

  /**
   * Restore a session for resuming recording
   * Loads session state from localStorage and sets up the manager
   * @param audioId The audio ID to restore
   * @returns true if successfully restored, false otherwise
   */
  async restoreSession(audioId: number): Promise<boolean> {
    const sessionKey = `resumableUpload_${audioId}`;
    const savedSession = localStorage.getItem(sessionKey);

    if (!savedSession) {
      console.log(`[RESTORE] No session found for audioId: ${audioId}`);
      return false;
    }

    try {
      const session = JSON.parse(savedSession) as SessionData;

      // Restore all session state
      this.sessionUri = session.sessionUri;
      this.audioId = session.audioId;
      this.uploadedBytes = session.uploadedBytes || 0;
      this.contentType = session.contentType;
      this.recordingTime = session.recordingTime || 0;
      this.wasResumed = true; // Mark as resumed to trigger remux on backend
      this.skipGcsUploads = false; // Reset resilient mode
      this.containerSegments = []; // Start fresh
      this.isFinalized = false;
      this.uploadInProgress = false;

      console.log(
        `[RESTORE] Session restored for audioId: ${audioId}, resuming from ${this.uploadedBytes} bytes, ${this.recordingTime}s (wasResumed: true)`,
      );
      return true;
    } catch (error) {
      console.error(`[RESTORE] Failed to restore session for audioId ${audioId}`, error);
      return false;
    }
  }

  /**
   * Finalize a GCS upload using only what's already in GCS (no IndexedDB)
   * This is used as a fallback when IndexedDB is unavailable or corrupted
   */
  private async finalizeGCSOnly(): Promise<void> {
    console.log("Starting GCS-only finalization (no IndexedDB)");

    if (!this.sessionUri) {
      throw new Error("No session URI available for GCS-only finalization");
    }

    // Step 1: Check upload status to get how many bytes GCS has
    const statusResponse = await fetch(this.sessionUri, {
      method: "PUT",
      headers: {
        "Content-Length": "0",
        "Content-Range": "bytes */*",
      },
    });

    console.log(`GCS status check response: ${statusResponse.status}`);

    // Handle different response codes
    if (statusResponse.status === 200 || statusResponse.status === 201) {
      // Upload is already complete
      console.log("GCS upload is already complete");
      return;
    }

    if (statusResponse.status === 308) {
      // Upload is incomplete, get the range
      const rangeHeader = statusResponse.headers.get("Range");
      console.log(`GCS upload incomplete. Range header: ${rangeHeader}`);

      if (!rangeHeader) {
        throw new Error("Upload incomplete but no Range header received from GCS");
      }

      // Parse the range to get bytes uploaded
      // Format: "bytes=0-88604671" means 88604672 bytes uploaded
      let bytesUploaded = 0;
      try {
        const rangeMatch = rangeHeader.match(/bytes=0-(\d+)/);
        if (rangeMatch?.[1]) {
          bytesUploaded = Number.parseInt(rangeMatch[1], 10) + 1;
          console.log(`GCS has ${bytesUploaded} bytes uploaded`);
        } else {
          throw new Error(`Could not parse Range header: ${rangeHeader}`);
        }
      } catch (parseError) {
        console.error("Error parsing Range header:", parseError);
        throw new Error(`Invalid Range header format: ${rangeHeader}`);
      }

      // Step 2: Send finalization request to complete the upload with what GCS has
      console.log(`Attempting to finalize GCS upload with ${bytesUploaded} bytes...`);

      const finalizeResponse = await fetch(this.sessionUri, {
        method: "PUT",
        headers: {
          "Content-Length": "0",
          "Content-Range": `bytes */${bytesUploaded}`,
        },
      });

      console.log(`GCS finalization response: ${finalizeResponse.status}`);

      if (finalizeResponse.status === 200 || finalizeResponse.status === 201) {
        console.log(`Successfully finalized GCS upload with ${bytesUploaded} bytes`);
        return;
      }

      if (finalizeResponse.status === 308) {
        // Still incomplete - this means GCS needs more data, but we don't have it
        throw new Error(
          `GCS upload could not be finalized (still incomplete after ${bytesUploaded} bytes). More data required.`,
        );
      }

      // Other error status
      const errorText = await finalizeResponse.text();
      throw new Error(`GCS finalization failed with status ${finalizeResponse.status}: ${errorText}`);
    }

    if (statusResponse.status === 404 || statusResponse.status === 410) {
      throw new Error(`GCS upload session expired or not found (status ${statusResponse.status})`);
    }

    // Unexpected status code
    const errorText = await statusResponse.text();
    throw new Error(`Unexpected GCS status ${statusResponse.status}: ${errorText}`);
  }

  // ============================================================================
  // ISOLATED METHODS FOR finalizePartialUpload
  // These methods operate on an IsolatedSessionContext instead of modifying
  // the singleton's state, allowing concurrent finalization operations.
  // ============================================================================

  /**
   * Check upload status using isolated context (doesn't modify singleton state)
   */
  private async checkUploadStatusIsolated(ctx: IsolatedSessionContext): Promise<number> {
    const statusResponse = await fetch(ctx.sessionUri, {
      method: "PUT",
      headers: {
        "Content-Range": "bytes */*",
      },
    });

    if (statusResponse.status === 308) {
      const range = statusResponse.headers.get("Range");
      if (range) {
        const match = range.match(/bytes=0-(\d+)/);
        if (match) {
          const serverAcknowledgedBytes = Number.parseInt(match[1], 10) + 1;
          console.log(`[ISOLATED] Upload status checked: ${serverAcknowledgedBytes} bytes on GCS`);
          return serverAcknowledgedBytes;
        }
      }
      // 308 without Range header means 0 bytes uploaded
      console.log("[ISOLATED] Received 308 without Range header - GCS has 0 bytes");
      return 0;
    }

    if (statusResponse.status === 200 || statusResponse.status === 201) {
      console.log("[ISOLATED] Upload already complete");
      return ctx.uploadedBytes; // Return current value, upload is done
    }

    if (statusResponse.status === 400) {
      const errorBody = await statusResponse.text();
      if (errorBody.includes("262144") && errorBody.includes("bytes uploaded is required")) {
        throw new Error("GCS_SESSION_CORRUPTED_ALIGNMENT");
      }
      throw new Error(`Unexpected status ${statusResponse.status}: ${errorBody}`);
    }

    if (statusResponse.status === 404 || statusResponse.status === 410) {
      throw new Error(`Upload session not found or expired (status ${statusResponse.status})`);
    }

    const errorBody = await statusResponse.text();
    throw new Error(`Unexpected status ${statusResponse.status}: ${errorBody}`);
  }

  /**
   * Upload an aligned chunk using isolated context (doesn't modify singleton state)
   * Returns the new uploadedBytes value after successful upload
   */
  private async uploadAlignedChunkIsolated(ctx: IsolatedSessionContext, exactBytesToUpload: number): Promise<number> {
    if (ctx.containerSegments.length === 0) {
      throw new Error("No container segments to upload");
    }

    if (exactBytesToUpload % this.MIN_CHUNK_SIZE !== 0) {
      throw new Error(`Upload size must be multiple of 256KB. Got ${exactBytesToUpload} bytes`);
    }

    // Create aligned blob from segments
    const combinedBlob = new Blob(ctx.containerSegments);
    const alignedBlob = combinedBlob.slice(0, exactBytesToUpload);

    const startByte = ctx.uploadedBytes;
    const endByte = startByte + exactBytesToUpload - 1;

    console.log(`[ISOLATED] Uploading aligned chunk: bytes ${startByte}-${endByte} (${exactBytesToUpload} bytes)`);

    const retryDelays = [2000, 4000];
    const maxAttempts = 3;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetch(ctx.sessionUri, {
          method: "PUT",
          headers: {
            "Content-Range": `bytes ${startByte}-${endByte}/*`,
            "Content-Type": ctx.contentType,
          },
          body: alignedBlob,
        });

        if (response.status === 308) {
          const rangeHeader = response.headers.get("Range");
          let serverConfirmedBytes = ctx.uploadedBytes;

          if (rangeHeader) {
            const match = rangeHeader.match(/bytes=0-(\d+)/);
            if (match) {
              serverConfirmedBytes = Number.parseInt(match[1], 10) + 1;
            }
          }

          const expectedBytes = ctx.uploadedBytes + exactBytesToUpload;
          if (serverConfirmedBytes >= expectedBytes) {
            // Update context's containerSegments (remove uploaded portion)
            const remainderBlob = combinedBlob.slice(exactBytesToUpload);
            ctx.containerSegments = remainderBlob.size > 0 ? [remainderBlob] : [];
            console.log(`[ISOLATED] Aligned upload successful. Server confirmed: ${serverConfirmedBytes} bytes`);
            return serverConfirmedBytes;
          }

          throw new Error(`Server only confirmed ${serverConfirmedBytes} bytes, expected ${expectedBytes}`);
        }

        if (response.status === 200 || response.status === 201) {
          const actualTotalSize = ctx.uploadedBytes + exactBytesToUpload;
          ctx.containerSegments = [];
          console.log(`[ISOLATED] Upload completed. Total size: ${actualTotalSize} bytes`);
          return actualTotalSize;
        }

        if ([503, 500, 502].includes(response.status)) {
          if (attempt < maxAttempts) {
            const delay = retryDelays[attempt - 1];
            console.log(
              `[ISOLATED][RETRY ${attempt}/${maxAttempts - 1}] Got ${response.status}, retrying after ${delay}ms...`,
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
          const errorText = await response.text();
          throw new Error(`All retries failed: HTTP ${response.status}: ${errorText}`);
        }

        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      } catch (error) {
        if (error instanceof Error && error.message.includes("HTTP")) {
          throw error;
        }

        if (attempt < maxAttempts) {
          const delay = retryDelays[attempt - 1];
          console.log(
            `[ISOLATED][RETRY ${attempt}/${maxAttempts - 1}] Network error, retrying after ${delay}ms...`,
            error,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }

    throw new Error("Upload failed after all retry attempts");
  }

  /**
   * Upload final chunk using isolated context (doesn't modify singleton state)
   * Returns the new uploadedBytes value after successful upload
   */
  private async uploadFinalChunkIsolated(ctx: IsolatedSessionContext): Promise<number> {
    if (ctx.containerSegments.length === 0) {
      console.log("[ISOLATED] No final chunks to upload");
      return ctx.uploadedBytes;
    }

    const finalBlob = new Blob(ctx.containerSegments);
    const finalSize = finalBlob.size;

    if (finalSize === 0) {
      console.log("[ISOLATED] Final chunk is empty, skipping");
      return ctx.uploadedBytes;
    }

    const startByte = ctx.uploadedBytes;
    const endByte = startByte + finalSize - 1;
    const actualTotalSize = ctx.uploadedBytes + finalSize;
    const contentRange = `bytes ${startByte}-${endByte}/${actualTotalSize}`;

    console.log(`[ISOLATED] Final chunk Content-Range: ${contentRange}`);

    const response = await fetch(ctx.sessionUri, {
      method: "PUT",
      headers: {
        "Content-Range": contentRange,
        "Content-Type": ctx.contentType,
      },
      body: finalBlob,
    });

    if (response.status === 200 || response.status === 201) {
      ctx.containerSegments = [];
      console.log("[ISOLATED] Final chunk uploaded successfully. Upload complete.");
      return actualTotalSize;
    }

    if (response.status === 308) {
      ctx.containerSegments = [];
      console.log("[ISOLATED] Final chunk uploaded (308 response)");
      return ctx.uploadedBytes + finalSize;
    }

    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  /**
   * Sync from IndexedDB using isolated context (doesn't modify singleton state)
   */
  private async syncFromIndexedDBIsolated(ctx: IsolatedSessionContext): Promise<void> {
    console.log("[ISOLATED] Syncing complete audio from IndexedDB to GCS");

    const fullBlob = await assembleBlob(ctx.audioId.toString());
    if (!fullBlob || fullBlob.size === 0) {
      throw new Error("No audio data found in IndexedDB");
    }

    const totalBytes = fullBlob.size;
    console.log(`[ISOLATED] IndexedDB contains ${totalBytes} bytes total`);
    ctx.totalFileSize = totalBytes;

    // Check what GCS actually has
    try {
      ctx.uploadedBytes = await this.checkUploadStatusIsolated(ctx);
      console.log(`[ISOLATED] GCS currently has ${ctx.uploadedBytes} bytes`);
    } catch (error) {
      if (error instanceof Error && error.message === "GCS_SESSION_CORRUPTED_ALIGNMENT") {
        console.warn("[ISOLATED] GCS session corrupted due to alignment issues, starting fresh upload");
        ctx.uploadedBytes = 0;
      } else {
        console.warn("[ISOLATED] GCS upload session is invalid, starting fresh upload:", error);
        ctx.uploadedBytes = 0;
      }
    }

    if (ctx.uploadedBytes >= totalBytes) {
      console.log("[ISOLATED] GCS already has all the data, no sync needed");
      return;
    }

    // Upload missing portion
    const missingBytes = totalBytes - ctx.uploadedBytes;
    const missingData = fullBlob.slice(ctx.uploadedBytes);
    console.log(`[ISOLATED] Uploading ${missingBytes} missing bytes from IndexedDB`);

    ctx.containerSegments = [missingData];

    // Upload in aligned chunks
    while (ctx.containerSegments.length > 0) {
      const pendingSize = ctx.containerSegments.reduce((total, segment) => total + segment.size, 0);

      if (pendingSize === 0) break;

      if (this.shouldUploadAlignedChunk(pendingSize)) {
        const alignedSize = this.calculateAlignedChunkSize(pendingSize);
        console.log(`[ISOLATED] Uploading aligned sync chunk: ${alignedSize} bytes`);
        ctx.uploadedBytes = await this.uploadAlignedChunkIsolated(ctx, alignedSize);
      } else {
        console.log(`[ISOLATED] Uploading final sync chunk: ${pendingSize} bytes`);
        ctx.uploadedBytes = await this.uploadFinalChunkIsolated(ctx);
        break;
      }
    }

    console.log("[ISOLATED] IndexedDB sync completed successfully");
  }

  /**
   * Finalize GCS upload using only what's in GCS (isolated version)
   */
  private async finalizeGCSOnlyIsolated(ctx: IsolatedSessionContext): Promise<void> {
    console.log("[ISOLATED] Starting GCS-only finalization (no IndexedDB)");

    const statusResponse = await fetch(ctx.sessionUri, {
      method: "PUT",
      headers: {
        "Content-Length": "0",
        "Content-Range": "bytes */*",
      },
    });

    console.log(`[ISOLATED] GCS status check response: ${statusResponse.status}`);

    if (statusResponse.status === 200 || statusResponse.status === 201) {
      console.log("[ISOLATED] GCS upload is already complete");
      return;
    }

    if (statusResponse.status === 308) {
      const rangeHeader = statusResponse.headers.get("Range");
      console.log(`[ISOLATED] GCS upload incomplete. Range header: ${rangeHeader}`);

      if (!rangeHeader) {
        throw new Error("Upload incomplete but no Range header received from GCS");
      }

      let bytesUploaded = 0;
      const rangeMatch = rangeHeader.match(/bytes=0-(\d+)/);
      if (rangeMatch?.[1]) {
        bytesUploaded = Number.parseInt(rangeMatch[1], 10) + 1;
        console.log(`[ISOLATED] GCS has ${bytesUploaded} bytes uploaded`);
      } else {
        throw new Error(`Could not parse Range header: ${rangeHeader}`);
      }

      console.log(`[ISOLATED] Attempting to finalize GCS upload with ${bytesUploaded} bytes...`);

      const finalizeResponse = await fetch(ctx.sessionUri, {
        method: "PUT",
        headers: {
          "Content-Length": "0",
          "Content-Range": `bytes */${bytesUploaded}`,
        },
      });

      console.log(`[ISOLATED] GCS finalization response: ${finalizeResponse.status}`);

      if (finalizeResponse.status === 200 || finalizeResponse.status === 201) {
        console.log(`[ISOLATED] Successfully finalized GCS upload with ${bytesUploaded} bytes`);
        return;
      }

      if (finalizeResponse.status === 308) {
        throw new Error(
          `GCS upload could not be finalized (still incomplete after ${bytesUploaded} bytes). More data required.`,
        );
      }

      const errorText = await finalizeResponse.text();
      throw new Error(`GCS finalization failed with status ${finalizeResponse.status}: ${errorText}`);
    }

    if (statusResponse.status === 404 || statusResponse.status === 410) {
      throw new Error(`GCS upload session expired or not found (status ${statusResponse.status})`);
    }

    const errorText = await statusResponse.text();
    throw new Error(`Unexpected GCS status ${statusResponse.status}: ${errorText}`);
  }
}

export const resumableUploadManager = new ResumableUploadManager();
